import React, { useEffect, useState } from "react";
import { Loader2, Play, Share2, Trash2, Music4, Sparkles, Check, Copy, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useStudio } from "@/studio/StudioContext";
import { useAuth } from "@/auth/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MySongs() {
  const { listProjects, loadProject, deleteProject, shareProjectById, generateCover, setActiveTab } = useStudio();
  const { user, setShowAuth } = useAuth();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [busy, setBusy] = useState({}); // id -> "share" | "cover"
  const [copiedId, setCopiedId] = useState(null);
  const signedIn = !!(user && user.id);

  const fetchList = async () => {
    if (!signedIn) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const list = await listProjects();
      setProjects(list);
    } catch (e) {
      if (e.response?.status === 401) setShowAuth(true);
      else toast.error("Could not load your songs");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn]);

  const open = (p) => {
    loadProject(p);
    setActiveTab("beats");
    toast.success(`Loaded "${p.name}"`);
  };

  const doDelete = async (p) => {
    try {
      await deleteProject(p.id);
      setProjects((prev) => prev.filter((x) => x.id !== p.id));
      toast.success("Song deleted");
    } catch (_) {
      toast.error("Could not delete song");
    }
  };

  const doShare = async (p) => {
    setBusy((b) => ({ ...b, [p.id]: "share" }));
    try {
      const data = await shareProjectById(p.id);
      const url = `${window.location.origin}/s/${data.share_id}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      if (data.cover_url) {
        setProjects((prev) => prev.map((x) => (x.id === p.id ? { ...x, cover_url: data.cover_url } : x)));
      }
      setCopiedId(p.id);
      setTimeout(() => setCopiedId(null), 2500);
      toast.success("Share link copied — with AI cover art");
    } catch (_) {
      toast.error("Could not create a share link");
    }
    setBusy((b) => ({ ...b, [p.id]: null }));
  };

  const doCover = async (p) => {
    setBusy((b) => ({ ...b, [p.id]: "cover" }));
    toast.info("Generating AI cover art…");
    try {
      const cover_url = await generateCover(p.id);
      setProjects((prev) => prev.map((x) => (x.id === p.id ? { ...x, cover_url } : x)));
      toast.success("Cover art ready — it shows on your share link");
    } catch (e) {
      toast.error("Could not generate cover art right now");
    }
    setBusy((b) => ({ ...b, [p.id]: null }));
  };

  return (
    <div className="h-full overflow-y-auto p-5 md:p-8" data-testid="my-songs">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display font-black text-2xl md:text-3xl tracking-wide flex items-center gap-2">
              <Music4 size={22} className="text-[#00e5ff]" /> My Songs
            </h1>
            <p className="text-neutral-500 text-sm mt-1">Tap a song to reopen it, share it, or give it AI cover art.</p>
          </div>
          <button
            data-testid="songs-refresh-btn"
            onClick={fetchList}
            className="w-9 h-9 rounded-sm border border-[#262626] flex items-center justify-center text-neutral-400 hover:text-[#00e5ff] hover:border-[#00e5ff] transition-colors shrink-0"
            title="Refresh"
          >
            <RefreshCw size={15} />
          </button>
        </div>

        {!signedIn ? (
          <div className="text-center py-20">
            <p className="text-neutral-400 mb-4">Sign in to see the songs you've saved.</p>
            <button
              data-testid="songs-signin-btn"
              onClick={() => setShowAuth(true)}
              className="px-5 py-2.5 rounded-sm border border-[#00e5ff] text-[#00e5ff] font-bold text-sm hover:bg-[#00e5ff]/10 transition-colors"
            >
              Sign In
            </button>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center gap-2 text-neutral-400 py-20">
            <Loader2 className="animate-spin" size={18} /> Loading your songs…
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-20">
            <Music4 size={40} className="text-neutral-700 mx-auto mb-3" />
            <p className="text-neutral-400">No saved songs yet. Make a beat and hit Save!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((p) => {
              const state = busy[p.id];
              return (
                <div
                  key={p.id}
                  data-testid={`song-card-${p.id}`}
                  className="rounded-lg border border-[#262626] bg-[#0d0d0d] overflow-hidden flex flex-col"
                >
                  <button
                    onClick={() => open(p)}
                    data-testid={`song-open-${p.id}`}
                    className="relative aspect-square w-full group"
                    style={{
                      background: p.cover_url
                        ? "#000"
                        : "linear-gradient(135deg,#0f1c24,#1a0f24)",
                    }}
                  >
                    {p.cover_url ? (
                      <img
                        src={`${BACKEND_URL}${p.cover_url}`}
                        alt={p.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Music4 size={40} className="text-neutral-700" />
                      </div>
                    )}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-colors">
                      <div className="w-14 h-14 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ background: "linear-gradient(120deg,#00e5ff,#a855f7)" }}>
                        <Play size={24} fill="black" className="text-black ml-0.5" />
                      </div>
                    </div>
                  </button>

                  <div className="p-3 flex flex-col gap-2 flex-1">
                    <div className="min-h-[38px]">
                      <div className="font-semibold text-sm text-neutral-100 truncate">{p.name}</div>
                      <div className="font-mono-data text-[10px] text-neutral-500">
                        {p.data?.bpm || "—"} BPM · {new Date(p.updated_at || p.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 mt-auto">
                      <button
                        data-testid={`song-share-${p.id}`}
                        onClick={() => doShare(p)}
                        disabled={state === "share"}
                        className="flex-1 flex items-center justify-center gap-1.5 h-8 rounded-sm border border-[#404040] text-[11px] font-bold text-neutral-200 hover:bg-[#1e1e1e] transition-colors disabled:opacity-50"
                      >
                        {state === "share" ? (
                          <Loader2 size={13} className="animate-spin" />
                        ) : copiedId === p.id ? (
                          <Check size={13} className="text-[#00ff66]" />
                        ) : (
                          <Share2 size={13} />
                        )}
                        {copiedId === p.id ? "Copied" : "Share"}
                      </button>
                      <button
                        data-testid={`song-cover-${p.id}`}
                        onClick={() => doCover(p)}
                        disabled={state === "cover"}
                        title="Generate AI cover art for the share card"
                        className="flex items-center justify-center gap-1.5 h-8 px-2.5 rounded-sm border border-[#ff007f55] text-[11px] font-bold text-[#ff007f] hover:bg-[#ff007f]/10 transition-colors disabled:opacity-50"
                      >
                        {state === "cover" ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                        Cover
                      </button>
                      <button
                        data-testid={`song-delete-${p.id}`}
                        onClick={() => doDelete(p)}
                        className="w-8 h-8 shrink-0 rounded-sm border border-[#404040] flex items-center justify-center text-neutral-500 hover:text-[#ff3333] hover:border-[#ff3333] transition-colors"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
